// src/pages/ClubForfeo.tsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { jsPDF } from 'jspdf';
import {
  Plane, Map, Sparkles, LogOut, FileText, Star, Trash2,
  Bed, ExternalLink, Calendar, Heart, Trophy, ChevronRight, Loader2, Download, MapPin, Hotel, Music,
  Zap, Bell, ArrowRight, X, LayoutDashboard, Newspaper, Store, Tag, MessageCircle,
  Gift, QrCode, ShieldCheck, Eye, ShoppingBag, CheckCircle, Package, Clock, Ticket,
  Globe, Mic, Volume2, BookOpen, MessageSquare, Send, Flame,
  List, CalendarCheck, Navigation
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ChatSystem from '../components/ChatSystem';
import LanguageAssistant from '../components/LanguageAssistant';

const ClubForfeo = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [profile, setProfile] = useState<any>(null);
  const [sessionUser, setSessionUser] = useState<any>(null);

  // --- ÉTATS ---
  const [favorites, setFavorites] = useState<any[]>([]);
  const [myPassports, setMyPassports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // --- Données pour les Commandes et Réservations ---
  const [orders, setOrders] = useState<any[]>([]);
  const [internalBookings, setInternalBookings] = useState<any[]>([]);
  const [unifiedBookings, setUnifiedBookings] = useState<any[]>([]);

  // --- NOUVEAU: Données Forfeo Finds ---
  const [findsFavorites, setFindsFavorites] = useState<any[]>([]);
  const [myAlerts, setMyAlerts] = useState<any[]>([]);
  const [seenKeys, setSeenKeys] = useState<Set<string>>(new Set());

  const initialTab = window.location.pathname.includes('/messages') ? 'messages' : (searchParams.get('tab') || 'dashboard');
  const [activeTab, setActiveTab] = useState(initialTab);

  const [newsFeed, setNewsFeed] = useState<any[]>([]);
  const [localPosts, setLocalPosts] = useState<any[]>([]);
  const [rewardsList, setRewardsList] = useState<any[]>([]);
  const [selectedNews, setSelectedNews] = useState<any>(null);

  const [showMyCard, setShowMyCard] = useState(false);
  const [pointNotification, setPointNotification] = useState<string | null>(null);

  // --- ÉTATS POUR LES AVIS ---
  const [reviewModal, setReviewModal] = useState<{ isOpen: boolean; order: any; forfaitId: string; rating: number; comment: string }>({
    isOpen: false, order: null, forfaitId: '', rating: 5, comment: ''
  });
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);

  // --- États pour l'onglet Langues ---
  const [showLanguageGuide, setShowLanguageGuide] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(true);
  const [preferredVoice, setPreferredVoice] = useState<string>('verse'); 

  const voiceOptions = [
    { id: 'verse', label: 'Verse (ultra naturel)' },
    { id: 'marin', label: 'Marin (calme, chaleureux)' },
    { id: 'cedar', label: 'Cedar (riche, posé)' },
    { id: 'ballad', label: 'Ballad (storytelling)' },
    { id: 'coral', label: 'Coral (clair, moderne)' },
    { id: 'sage', label: 'Sage (pro, doux)' },
    { id: 'alloy', label: 'Alloy (polyvalent)' },
    { id: 'nova', label: 'Nova (énergique)' },
    { id: 'shimmer', label: 'Shimmer (léger)' },
    { id: 'echo', label: 'Echo (net)' },
    { id: 'fable', label: 'Fable (expressif)' },
    { id: 'onyx', label: 'Onyx (grave)' },
    { id: 'ash', label: 'Ash (posé)' },
  ];

  const legacyTravelerTabRoutes: Record<string, string> = {
    trips: '/mes-achats',
    billets: '/mes-achats',
    purchases: '/mes-achats',
    achats: '/mes-achats',
    favorites: '/mes-favoris',
    favoris: '/mes-favoris',
    reservations: '/mes-reservations',
    alertes: '/mes-alertes',
    alerts: '/mes-alertes',
  };

  const goToTravelerPage = (path: string) => {
    try {
      localStorage.setItem('forfeo_last_portal_path', path);
      localStorage.setItem('forfeo_last_portal_label', 'Espace voyageur');
      localStorage.setItem('forfeo_last_traveler_path', path);
    } catch {
      // stockage local non critique
    }
    navigate(path);
  };

  useEffect(() => {
    const target = legacyTravelerTabRoutes[activeTab];
    if (target) {
      try {
        localStorage.setItem('forfeo_last_portal_path', target);
        localStorage.setItem('forfeo_last_portal_label', 'Espace voyageur');
        localStorage.setItem('forfeo_last_traveler_path', target);
      } catch {
        // stockage local non critique
      }
      navigate(target, { replace: true });
    }
  }, [activeTab, navigate]);

  const travelerHubCards = [
    {
      title: 'Tableau voyageur',
      subtitle: 'Vue complète de vos alertes, achats, réservations et favoris.',
      path: '/dashboard-voyageur',
      icon: <LayoutDashboard size={24} />,
      accent: 'bg-slate-900 text-white',
    },
    {
      title: 'Découvrir le Québec',
      subtitle: 'Guide FR/EN pour préparer un séjour, explorer les régions et trouver des expériences locales.',
      path: '/decouvrir-quebec',
      icon: <Globe size={24} />,
      accent: 'bg-emerald-600 text-white',
    },
    {
      title: 'Mes achats & vouchers',
      subtitle: 'Billets QR, reçus, taxes et statuts d’utilisation.',
      path: '/mes-achats',
      icon: <ShoppingBag size={24} />,
      accent: 'bg-orange-500 text-white',
    },
    {
      title: 'Mes réservations',
      subtitle: 'Last Call, expériences confirmées et réservations à venir.',
      path: '/mes-reservations',
      icon: <CalendarCheck size={24} />,
      accent: 'bg-blue-600 text-white',
    },
    {
      title: 'Mes favoris',
      subtitle: 'Lieux, offres et idées sauvegardées pour plus tard.',
      path: '/mes-favoris',
      icon: <Heart size={24} />,
      accent: 'bg-pink-500 text-white',
    },
    {
      title: 'Alertes Évasion',
      subtitle: 'Suivi des aubaines voyage et alertes intelligentes.',
      path: '/mes-alertes',
      icon: <Plane size={24} className="-rotate-45" />,
      accent: 'bg-sky-500 text-white',
    },
    {
      title: 'Messages partenaires',
      subtitle: 'Conversations et échanges avec les commerces locaux.',
      path: '/dashboard-voyageur/messages',
      icon: <MessageCircle size={24} />,
      accent: 'bg-indigo-600 text-white',
    },
  ];

  // --- 1. DÉTECTION DU CONTACT ---
  useEffect(() => {
    const contactId = searchParams.get('contact');
    if (!contactId || !sessionUser) return;
    setActiveTab('messages');
    startChatWithBusiness(contactId);
  }, [searchParams, sessionUser]);

  // CORRECTION: Chargement de l'historique local (seenKeys)
  useEffect(() => {
    try {
      const raw = localStorage.getItem("forfeo_finds_seen_keys");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          setSeenKeys(new Set(parsed.map((x) => String(x))));
        }
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    fetchClubData();
  }, []);

  const fetchClubData = async () => {
    try {
      setLoading(true);
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session) {
        navigate('/login');
        return;
      }
      setSessionUser(session.user);
      const userId = session.user.id;

      // Profil
      const { data: profileData } = await supabase.from('traveler_profiles').select('*').eq('id', userId).maybeSingle();
      setProfile(profileData || { email: session.user.email, membership_level: 'Membre', points_total: 0, full_name: session.user.user_metadata?.full_name });

      // Favoris Classiques
      const { data: favData } = await supabase.from('favorites').select('*').eq('user_id', userId).order('created_at', { ascending: false });
      setFavorites(favData || []);

      // Passeports
      const { data: passportData } = await supabase.from('passports').select('*').eq('user_id', userId).order('created_at', { ascending: false });
      setMyPassports(passportData || []);

      // Actu
      const { data: newsData } = await supabase.from('announcements').select('*').eq('is_active', true).order('created_at', { ascending: false }).limit(5);
      setNewsFeed(newsData || []);

      // Posts Locaux
      const { data: postsData } = await supabase.from('posts').select('*, businesses(id, name, image_url)').order('created_at', { ascending: false }).limit(20);
      setLocalPosts(postsData || []);

      // Récompenses
      const { data: rewardsData } = await supabase.from('rewards').select('*, businesses(name)').eq('is_active', true).order('cost_points', { ascending: true });
      setRewardsList(rewardsData || []);

      // Commandes
      const { data: ordersData } = await supabase.from('shop_orders').select('*, business:businesses(name, image_url, region)').eq('user_id', userId).order('created_at', { ascending: false });
      setOrders(ordersData || []);

      // Réservations INTERNES
      const { data: internalData } = await supabase.from('bookings').select(`*, business:businesses(name, region, image_url)`).eq('user_id', userId).order('check_in_date', { ascending: false });
      setInternalBookings(internalData || []);

      // Réservations UNIFIÉES
      const { data: unifiedData } = await supabase.from('unified_reservations').select(`*, business:businesses(name, region, image_url)`).eq('user_id', userId).order('start_at', { ascending: false });
      setUnifiedBookings(unifiedData || []);

      // NOUVEAU : Récupération des favoris Forfeo Finds
      await fetchFindsFavorites(userId);

      // NOUVEAU : Récupération des Alertes Forfeo Finds
      const { data: alertsData } = await supabase.from('finds_alerts').select('*').eq('user_id', userId).order('created_at', { ascending: false });
      setMyAlerts(alertsData || []);

    } catch (err) {
      console.error("Erreur Club Forfeo:", err);
    } finally {
      setLoading(false);
    }
  };

  // --- FETCH FINDS FAVORITES ---
  const fetchFindsFavorites = async (userId: string) => {
    try {
      const { data: favs } = await supabase.from('finds_favorites').select('*').eq('user_id', userId);
      if (!favs || favs.length === 0) {
        setFindsFavorites([]);
        return;
      }

      // Pour l'instant, on s'appuie sur la vue finds_public ou last_call_deals_public pour récupérer les détails
      const { data: allDeals } = await supabase.from('finds_public').select('*'); // Assure-toi que cette vue existe, sinon adapte selon ton BFF
      
      if (allDeals) {
        const enrichedFavs = favs.map(f => {
          const dealDetails = allDeals.find(d => (String(d.source_type || d.source) === f.source && String(d.source_id || d.id) === String(f.source_id)));
          return { ...f, details: dealDetails };
        }).filter(f => f.details); // On garde que ceux qui existent encore
        setFindsFavorites(enrichedFavs);
      }
    } catch (e) {
      console.error("Erreur chargement favoris Finds", e);
    }
  };

  const awardPoints = async (amount: number, action: string, description: string) => {
    if (!sessionUser) return;
    try {
      const { error } = await supabase.rpc('add_points', {
        p_user_id: sessionUser.id,
        p_amount: amount,
        p_action: action,
        p_desc: description
      });
      if (!error) {
        setProfile((prev: any) => ({ ...prev, points_total: (prev?.points_total || 0) + amount }));
        setPointNotification(`+${amount} PTS !`);
        setTimeout(() => setPointNotification(null), 2500);
      }
    } catch (e) {
      console.error("Erreur points:", e);
    }
  };

  const submitReview = async () => {
    if (!reviewModal.forfaitId) return;
    setIsSubmittingReview(true);
    
    try {
      const reviewerName = profile?.full_name || sessionUser?.user_metadata?.full_name || profile?.email?.split('@')[0] || 'Voyageur Forfeo';
      
      const { error } = await supabase.from('boutique_reviews').insert({
        forfait_id: reviewModal.forfaitId,
        user_id: sessionUser.id,
        user_name: reviewerName,
        rating: reviewModal.rating,
        comment: reviewModal.comment
      });

      if (error) throw error;

      alert("Merci pour votre avis !");
      setReviewModal({ isOpen: false, order: null, forfaitId: '', rating: 5, comment: '' });
      awardPoints(10, 'LEAVE_REVIEW', 'Avis laissé sur un forfait boutique');

    } catch (err: any) {
      alert("Erreur: " + err.message);
    } finally {
      setIsSubmittingReview(false);
    }
  };

  const deleteFavorite = async (id: any, e: any) => {
    e.preventDefault(); e.stopPropagation();
    const previousFavorites = [...favorites];
    setFavorites(favorites.filter(f => f.id !== id));
    const { error } = await supabase.from('favorites').delete().eq('id', id);
    if (error) setFavorites(previousFavorites);
  };

  // NOUVEAU : Supprimer un favori Finds
  const deleteFindsFavorite = async (favId: string, e: any) => {
    e.preventDefault(); e.stopPropagation();
    setFindsFavorites(findsFavorites.filter(f => f.id !== favId));
    await supabase.from('finds_favorites').delete().eq('id', favId);
  };

  // NOUVEAU : Supprimer une alerte
  const deleteAlert = async (alertId: string) => {
    if (!window.confirm("Supprimer cette alerte ?")) return;
    setMyAlerts(myAlerts.filter(a => a.id !== alertId));
    await supabase.from('finds_alerts').delete().eq('id', alertId);
  };

  const deletePassport = async (id: string) => {
    if (!window.confirm("Supprimer ?")) return;
    const { error } = await supabase.from('passports').delete().eq('id', id);
    if (!error) setMyPassports(myPassports.filter(p => p.id !== id));
  };

  const handleLogout = async () => { await supabase.auth.signOut(); navigate('/login'); };

  const handleVisitFavorite = (fav: any) => {
    if (fav.type === 'local_offer') {
      if (fav.data?.business_id) navigate(`/place/${fav.data.business_id}`);
      else alert("Impossible de localiser cette entreprise.");
    } else if (fav.type === 'destination') {
      navigate(`/last-call?dest=${fav.item_id}`);
    } else if (fav.type === 'hotel_location') {
      navigate(`/explore/hotel/${fav.item_id}`);
    } else if (fav.type === 'boutique_forfait') { 
      navigate(`/boutique/${fav.item_id}`);
    } else {
      navigate(`/explore/${fav.type || 'hotel'}/${fav.item_id}`, { state: { item: fav.data } });
    }
  };

  const toggleLikeLocalPost = async (post: any) => {
    const userId = sessionUser?.id;
    if (!userId) return;
    const existingFav = favorites.find(f => f.item_id === post.id && f.type === 'local_offer');

    if (existingFav) {
      await supabase.from('favorites').delete().eq('id', existingFav.id);
      setFavorites(favorites.filter(f => f.id !== existingFav.id));
    } else {
      const newFav = {
        user_id: userId,
        item_id: post.id,
        type: 'local_offer',
        data: {
          name: post.businesses?.name || "Offre Locale",
          image: post.image_url,
          location: "Réseau Forfeo",
          description: post.content,
          business_id: post.businesses?.id
        }
      };
      const { data } = await supabase.from('favorites').insert([newFav]).select().single();
      if (data) {
        setFavorites([data, ...favorites]);
        awardPoints(5, 'LIKE_OFFER', `Favori: ${post.businesses?.name}`);
      }
    }
  };

  const isPostLiked = (postId: any) => {
    return favorites.some(f => f.item_id === postId && f.type === 'local_offer');
  };

  const startChatWithBusiness = async (businessId: any) => {
    if (!businessId || !sessionUser) return;

    setActiveTab('messages');
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);

    try {
      const { data: existing } = await supabase
        .from('conversations')
        .select('id')
        .eq('traveler_id', sessionUser.id)
        .eq('business_id', businessId)
        .maybeSingle();

      const timestamp = new Date().toISOString();

      if (!existing) {
        const { error: createError } = await supabase
          .from('conversations')
          .insert([{
            traveler_id: sessionUser.id,
            business_id: businessId,
            last_message: "Contact via Forfeo Explore",
            updated_at: timestamp
          }]);

        if (!createError) awardPoints(25, 'START_CHAT', 'Contact Entreprise');
      } else {
        await supabase
          .from('conversations')
          .update({ updated_at: timestamp })
          .eq('id', existing.id);
      }
    } catch (e) {
      console.error("Erreur technique chat:", e);
    }
  };

  const downloadExistingPassport = (passport: any) => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 20; const contentWidth = pageWidth - 2 * margin;

      const addHeader = () => {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(22);
        doc.text("FORFEO Explore.", margin, 30);
        return 50;
      };

      let yPos = addHeader();
      const lines = (passport.full_content || "").split('\n');

      lines.forEach((line: string) => {
        if (yPos > pageHeight - 30) { doc.addPage(); yPos = addHeader(); }
        doc.setFontSize(10);
        doc.text(doc.splitTextToSize(line, contentWidth), margin, yPos);
        yPos += 7;
      });

      doc.save(`Passeport-Forfeo-${passport.doc_id}.pdf`);
    } catch (error) {
      alert("Erreur PDF");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-blue-600" size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 font-sans relative pb-20">
      <Header />

      {pointNotification && (
        <div className="fixed top-24 right-4 z-50 bg-yellow-400 text-slate-900 px-6 py-3 rounded-full font-black shadow-xl animate-bounce flex items-center gap-2 border-2 border-white transform hover:scale-110 transition">
          <Trophy size={20} /> {pointNotification}
        </div>
      )}

      {/* MODALE AVIS BOUTIQUE */}
      {reviewModal.isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden relative">
            <button onClick={() => setReviewModal({ isOpen: false, order: null, forfaitId: '', rating: 5, comment: '' })} className="absolute top-4 right-4 text-slate-400 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 p-2 rounded-full transition z-10"><X size={20} /></button>
            <div className="bg-yellow-400 p-8 text-center">
              <Star size={48} className="mx-auto text-yellow-100 fill-yellow-100 mb-2" />
              <h2 className="text-2xl font-black text-yellow-900">Évaluez votre expérience</h2>
              <p className="text-yellow-800 text-sm font-medium">Et gagnez 10 points Forfeo !</p>
            </div>
            <div className="p-8">
              <p className="text-center font-bold text-slate-700 mb-6">Comment s'est passé votre visite chez <br/><span className="text-slate-900 font-black">{reviewModal.order?.business?.name}</span> ?</p>
              
              <div className="flex justify-center gap-2 mb-8">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button 
                    key={star} 
                    onClick={() => setReviewModal(p => ({ ...p, rating: star }))}
                    className={`transition-all transform hover:scale-110 ${reviewModal.rating >= star ? 'text-yellow-400' : 'text-slate-200'}`}
                  >
                    <Star size={40} fill="currentColor" />
                  </button>
                ))}
              </div>

              <textarea 
                rows={4}
                value={reviewModal.comment}
                onChange={e => setReviewModal(p => ({ ...p, comment: e.target.value }))}
                placeholder="Racontez-nous ce que vous avez aimé... (Optionnel)"
                className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 font-medium text-slate-700 focus:outline-none focus:border-yellow-400 resize-none mb-6"
              />

              <button 
                onClick={submitReview}
                disabled={isSubmittingReview}
                className="w-full bg-slate-900 text-white font-black uppercase tracking-widest py-4 rounded-2xl hover:bg-slate-800 transition shadow-lg flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isSubmittingReview ? <Loader2 className="animate-spin" /> : <><Send size={18} /> Envoyer mon avis</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODALE CARTE MEMBRE QR */}
      {showMyCard && sessionUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/90 backdrop-blur-md animate-in zoom-in duration-200">
          <div className="bg-white rounded-3xl w-full max-w-sm shadow-2xl overflow-hidden relative">
            <button onClick={() => setShowMyCard(false)} className="absolute top-4 right-4 bg-white/20 hover:bg-white/40 p-2 rounded-full text-white z-20"><X size={20} /></button>
            <div className="bg-gradient-to-br from-blue-600 to-indigo-800 p-8 text-center text-white relative">
              <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
              <h2 className="text-2xl font-black uppercase tracking-tighter relative z-10 mb-1">Forfeo ID</h2>
              <p className="text-blue-200 text-xs font-medium relative z-10 uppercase tracking-widest">{profile?.membership_level || 'Membre'}</p>
            </div>
            <div className="p-8 text-center bg-white">
              <div className="bg-white p-4 rounded-2xl shadow-inner border border-slate-100 inline-block mb-6">
                <img src={`https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${sessionUser.id}&color=0f172a`} alt="QR Code Membre" className="w-48 h-48 mx-auto" />
              </div>
              <p className="text-xs text-slate-400 font-mono mb-2">ID: {sessionUser.id.substring(0, 8)}...</p>
              <p className="text-sm font-bold text-slate-800 px-4">Présentez ce code au commerçant pour utiliser vos points ou valider votre statut.</p>
              <div className="mt-6 pt-6 border-t border-slate-100">
                <div className="inline-flex flex-col items-center">
                  <span className="text-3xl font-black text-blue-600">{profile?.points_total}</span>
                  <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Points Disponibles</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODALE ACTU */}
      {selectedNews && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl overflow-hidden relative">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white flex justify-between items-start">
              <div>
                <span className="bg-white/20 text-white px-2 py-1 rounded text-[10px] font-black uppercase tracking-wider mb-2 inline-block">{selectedNews.tag || 'INFO'}</span>
                <h2 className="text-2xl font-black leading-tight">{selectedNews.title}</h2>
                <p className="text-white/80 text-xs mt-1">Publié par {selectedNews.author_name}</p>
              </div>
              <button onClick={() => setSelectedNews(null)} className="bg-white/20 hover:bg-white/40 p-2 rounded-full transition text-white"><X size={20} /></button>
            </div>
            <div className="p-8 max-h-[60vh] overflow-y-auto">
              <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-line">{selectedNews.content}</p>
              <div className="mt-8 flex justify-end">
                <button onClick={() => { setSelectedNews(null); awardPoints(2, 'READ_NEWS', 'Lecture Actualité'); }} className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition">
                  Fermer (+2 pts)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="py-10 px-4 md:px-8">

        {/* PROFIL CARD */}
        <div className="max-w-6xl mx-auto bg-white rounded-3xl p-8 shadow-sm border border-slate-100 flex flex-col md:flex-row justify-between items-center gap-6 mb-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-yellow-400 rounded-full blur-3xl opacity-10 -mr-20 -mt-20 pointer-events-none"></div>
          <div className="flex items-center gap-4 text-center md:text-left flex-col md:flex-row relative z-10">
            <div className="w-20 h-20 bg-slate-900 rounded-full flex items-center justify-center text-white text-3xl font-black shadow-xl ring-4 ring-blue-50 border-4 border-white">
              {profile?.email?.charAt(0).toUpperCase() || 'V'}
            </div>
            <div>
              <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tighter">Bonjour, {profile?.email?.split('@')[0]} !</h1>
              <div className="flex flex-wrap justify-center md:justify-start gap-2 mt-2">
                <span className="bg-blue-50 text-blue-700 text-[10px] font-black px-3 py-1 rounded-full border border-blue-100 flex items-center gap-1 uppercase tracking-wider">
                  <Trophy size={10} /> {profile?.membership_level || 'Membre'}
                </span>
                <span className="bg-yellow-100 text-yellow-800 text-[10px] font-black px-3 py-1 rounded-full border border-yellow-200 flex items-center gap-1 uppercase tracking-wider">
                  <Sparkles size={10} /> {profile?.points_total || 0} PTS
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-col md:flex-row gap-3 relative z-10">
            <button onClick={() => goToTravelerPage('/dashboard-voyageur')} className="flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-2xl text-xs font-black uppercase tracking-widest transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 border border-slate-800">
              <LayoutDashboard size={16} /> Mon tableau
            </button>
            <button onClick={() => goToTravelerPage('/mes-achats')} className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white px-6 py-3 rounded-2xl text-xs font-black uppercase tracking-widest transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 border border-orange-400">
              <ShoppingBag size={16} /> Mes achats
            </button>
            <button onClick={() => setShowMyCard(true)} className="flex items-center gap-2 bg-white text-slate-900 px-6 py-3 rounded-2xl text-xs font-black uppercase tracking-widest transition-all shadow-sm hover:shadow-md transform hover:-translate-y-0.5 border border-slate-200">
              <QrCode size={16} /> Carte membre
            </button>
            <button onClick={handleLogout} className="flex items-center gap-2 text-slate-500 hover:text-red-600 bg-slate-50 hover:bg-red-50 px-5 py-3 rounded-2xl text-xs font-black uppercase tracking-widest transition-all border border-slate-100">
              <LogOut size={16} />
            </button>
          </div>
        </div>

        {/* NAVIGATION TABS */}
        <div className="max-w-6xl mx-auto mb-8">
          <div className="bg-white p-1.5 rounded-2xl shadow-sm border border-slate-200 overflow-x-auto">
            <div className="flex gap-1 min-w-max px-2">
              <button onClick={() => setActiveTab('dashboard')} className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-all whitespace-nowrap ${activeTab === 'dashboard' ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'}`}><LayoutDashboard size={14} /> Hub Club</button>
              <button onClick={() => goToTravelerPage('/dashboard-voyageur')} className="flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-all whitespace-nowrap bg-slate-50 text-slate-700 hover:bg-slate-900 hover:text-white border border-slate-100"><LayoutDashboard size={14} /> Tableau voyageur</button>
              <button onClick={() => goToTravelerPage('/mes-achats')} className="flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-all whitespace-nowrap bg-orange-50 text-orange-600 hover:bg-orange-500 hover:text-white border border-orange-100"><ShoppingBag size={14} /> Achats & vouchers</button>
              <button onClick={() => goToTravelerPage('/mes-reservations')} className="flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-all whitespace-nowrap bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white border border-blue-100"><CalendarCheck size={14} /> Réservations</button>
              <button onClick={() => goToTravelerPage('/mes-favoris')} className="flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-all whitespace-nowrap bg-pink-50 text-pink-600 hover:bg-pink-500 hover:text-white border border-pink-100"><Heart size={14} /> Favoris</button>
              <button onClick={() => goToTravelerPage('/mes-alertes')} className="flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-all whitespace-nowrap bg-sky-50 text-sky-600 hover:bg-sky-500 hover:text-white border border-sky-100"><Plane size={14} /> Alertes</button>
              <button onClick={() => setActiveTab('rewards')} className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-all whitespace-nowrap ${activeTab === 'rewards' ? 'bg-yellow-500 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'}`}><Gift size={14} /> Récompenses</button>
              <button onClick={() => setActiveTab('local')} className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-all whitespace-nowrap ${activeTab === 'local' ? 'bg-emerald-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'}`}><Store size={14} /> Local</button>
              <button onClick={() => setActiveTab('news')} className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-all whitespace-nowrap ${activeTab === 'news' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'}`}><Newspaper size={14} /> Actu</button>
              <button onClick={() => setActiveTab('messages')} className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-all whitespace-nowrap ${activeTab === 'messages' ? 'bg-blue-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'}`}><MessageCircle size={14} /> Messages</button>
              <button onClick={() => setActiveTab('language')} className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-all whitespace-nowrap ${activeTab === 'language' ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'}`}><Globe size={14} /> Langues</button>
            </div>
          </div>
        </div>

        {/* TAB 1 : DASHBOARD */}
        {activeTab === 'dashboard' && (
          <div className="max-w-6xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
            
            <div className="mb-8 relative overflow-hidden rounded-[32px] bg-slate-950 text-white p-6 md:p-8 shadow-xl border border-slate-800">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(14,165,233,.35),transparent_34%),radial-gradient(circle_at_bottom_left,rgba(249,115,22,.22),transparent_30%)] pointer-events-none"></div>
              <div className="relative z-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                <div>
                  <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest text-blue-100 mb-4">
                    <ShieldCheck size={13} /> Hub voyageur Forfeo
                  </div>
                  <h2 className="text-2xl md:text-4xl font-black tracking-tight max-w-3xl">Le Club devient votre porte d’entrée, les actions importantes vivent dans des pages dédiées.</h2>
                  <p className="text-slate-300 text-sm md:text-base mt-3 max-w-3xl leading-relaxed">
                    Accédez rapidement à vos achats, billets QR, réservations, favoris et alertes sans mélanger les anciens onglets du Club avec les nouveaux espaces transactionnels.
                  </p>
                </div>
                <button onClick={() => goToTravelerPage('/dashboard-voyageur')} className="bg-white text-slate-950 px-6 py-4 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-blue-50 transition shadow-lg flex items-center justify-center gap-2">
                  Ouvrir mon espace voyageur <ArrowRight size={16} />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {travelerHubCards.map((card) => (
                <button
                  key={card.path}
                  onClick={() => goToTravelerPage(card.path)}
                  className="group text-left bg-white rounded-[28px] p-5 border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all"
                >
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 shadow-sm ${card.accent} group-hover:scale-105 transition-transform`}>
                    {card.icon}
                  </div>
                  <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight mb-1">{card.title}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">{card.subtitle}</p>
                </button>
              ))}
            </div>

            {/* --- NOUVEAU GRID DASHBOARD (Basé sur la capture d'écran) --- */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {/* 1. Scanner Code QR */}
              <div onClick={() => alert("Fonctionnalité Scanner de code QR")} className="bg-white rounded-[32px] p-6 shadow-sm border border-slate-100 flex flex-col items-center justify-center text-center gap-4 cursor-pointer hover:shadow-md transition-all aspect-square">
                <QrCode size={48} className="text-blue-600" />
                <span className="text-[14px] font-black text-slate-800 leading-tight">Scanner<br/>Code QR</span>
              </div>

              {/* 2. Réservations */}
              <div onClick={() => goToTravelerPage('/mes-reservations')} className="bg-white rounded-[32px] p-6 shadow-sm border border-slate-100 flex flex-col items-center justify-center text-center gap-4 cursor-pointer hover:shadow-md transition-all aspect-square relative">
                <div className="relative">
                  <div className="bg-[#2C3E50] text-white p-3 rounded-2xl">
                    <List size={32} strokeWidth={2.5} />
                  </div>
                  {/* Badge avec la longueur des réservations ou un statique */}
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[12px] font-black w-6 h-6 flex items-center justify-center rounded-full shadow-sm ring-2 ring-white">{internalBookings.length + unifiedBookings.length}</span>
                </div>
                <span className="text-[14px] font-black text-slate-800 leading-tight">Réservations</span>
              </div>

              {/* 3. Commandes Shop */}
              <div onClick={() => goToTravelerPage('/mes-achats')} className="bg-[#F4F6FF] rounded-[32px] p-6 shadow-sm border border-[#E0E7FF] flex flex-col items-center justify-center text-center gap-4 cursor-pointer hover:shadow-md transition-all aspect-square relative overflow-hidden group">
                <div className="absolute top-3 right-3 bg-orange-500 text-white text-[9px] font-black px-2 py-1 rounded-full uppercase tracking-widest shadow-sm">Nouveau</div>
                <ShoppingBag size={48} className="text-blue-600 group-hover:scale-110 transition-transform" fill="currentColor" />
                <span className="text-[14px] font-black text-slate-800 leading-tight">Mes achats<br/>& vouchers</span>
              </div>

              {/* 4. Calendrier Dispos */}
              <div onClick={() => alert("Fonctionnalité Calendrier Dispos")} className="bg-white rounded-[32px] p-6 shadow-sm border border-slate-100 flex flex-col items-center justify-center text-center gap-4 cursor-pointer hover:shadow-md transition-all aspect-square">
                <div className="text-[#2C3E50]">
                  <CalendarCheck size={48} strokeWidth={2} />
                </div>
                <span className="text-[14px] font-black text-slate-800 leading-tight">Calendrier<br/>Dispos</span>
              </div>

              {/* 5. Hébergement & Resto */}
              <div onClick={() => setActiveTab('local')} className="bg-white rounded-[32px] p-6 shadow-sm border border-slate-100 flex flex-col items-center justify-center text-center gap-4 cursor-pointer hover:shadow-md transition-all aspect-square">
                <div className="text-[#2C3E50]">
                  <Bed size={48} strokeWidth={2} />
                </div>
                <span className="text-[14px] font-black text-slate-800 leading-tight">Hébergement<br/>& Resto</span>
              </div>

              {/* 6. Forfeo Drops */}
              <div onClick={() => navigate('/drops')} className="bg-white rounded-[32px] p-6 shadow-sm border border-slate-100 flex flex-col items-center justify-center text-center gap-4 cursor-pointer hover:shadow-md transition-all aspect-square">
                <div className="text-[#2C3E50] transform rotate-45">
                  <Navigation size={44} fill="currentColor" strokeWidth={1} />
                </div>
                <span className="text-[14px] font-black text-slate-800 leading-tight mt-1">Forfeo<br/>Drops</span>
              </div>

              {/* 7. Plateforme Globale */}
              <div onClick={() => navigate('/explorer-plus?guests=2&category=hebergements_locaux')} className="bg-white rounded-[32px] p-6 shadow-sm border border-slate-100 flex flex-col items-center justify-center text-center gap-4 cursor-pointer hover:shadow-md transition-all aspect-square">
                <div className="text-[#2C3E50]">
                  <Globe size={48} strokeWidth={2} />
                </div>
                <span className="text-[14px] font-black text-slate-800 leading-tight">Plateforme<br/>Globale</span>
              </div>

              {/* 8. Alertes Évasion */}
              <div onClick={() => goToTravelerPage('/mes-alertes')} className="bg-white rounded-[32px] p-6 shadow-sm border border-slate-100 flex flex-col items-center justify-center text-center gap-4 cursor-pointer hover:shadow-md transition-all aspect-square">
                <div className="text-sky-500">
                  <Plane size={48} strokeWidth={2} className="-rotate-45" />
                </div>
                <span className="text-[14px] font-black text-slate-800 leading-tight">Alertes<br/>Évasion</span>
              </div>
            </div>

            <div className="mb-8 bg-gradient-to-br from-slate-950 via-blue-950 to-blue-700 rounded-[32px] p-6 md:p-8 text-white shadow-xl border border-blue-900/30 overflow-hidden relative">
              <div className="absolute -right-10 -top-10 w-48 h-48 bg-cyan-400/20 rounded-full blur-3xl pointer-events-none"></div>
              <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div>
                  <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest text-blue-100 mb-3">
                    <ShoppingBag size={13} /> Nouvel espace achat
                  </div>
                  <h2 className="text-2xl md:text-3xl font-black tracking-tight">Mes achats, reçus, taxes et vouchers</h2>
                  <p className="text-blue-100 text-sm md:text-base mt-2 max-w-2xl leading-relaxed">
                    Consultez vos commandes Forfeo, vos billets QR, vos taxes payées et vos preuves d’achat dans une page dédiée plus complète que l’ancien onglet Billets.
                  </p>
                </div>
                <button onClick={() => goToTravelerPage('/mes-achats')} className="shrink-0 bg-white text-slate-950 px-6 py-4 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-blue-50 transition shadow-lg flex items-center justify-center gap-2">
                  Ouvrir mes achats <ArrowRight size={16} />
                </button>
              </div>
            </div>

            {/* --- ANCIEN CONTENU DASHBOARD PRÉSERVÉ --- */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              <div className="lg:col-span-1 space-y-6">
                <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm relative overflow-hidden group">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-400 rounded-full blur-3xl opacity-20 -mr-16 -mt-16 pointer-events-none"></div>
                  <h2 className="text-lg font-black flex items-center gap-2 mb-2 uppercase tracking-tighter text-slate-900"><Sparkles size={18} className="text-yellow-500" fill="currentColor" /> Nouveau Voyage ?</h2>
                  <p className="text-slate-500 text-xs mb-6 leading-relaxed">Créez un itinéraire sur mesure grâce à notre IA.</p>
                  <Link to="/generateur-ia" className="bg-slate-900 text-white px-4 py-3 rounded-xl text-xs font-black hover:bg-slate-800 transition-all flex items-center justify-center gap-2 shadow-lg uppercase tracking-widest w-full">
                    Générateur IA
                  </Link>
                </div>

                <div>
                  <h3 className="font-black text-slate-900 flex items-center gap-2 mb-4 px-2 uppercase tracking-widest text-xs"><FileText size={16} className="text-blue-600" /> Mes Passeports PDF</h3>
                  {myPassports.length > 0 ? (
                    <div className="space-y-3">
                      {myPassports.map((pass) => (
                        <div key={pass.id} className="bg-white rounded-2xl p-4 border border-slate-200 hover:shadow-md transition-all group relative">
                          <div className="flex justify-between items-start mb-2 pr-6">
                            <span className="text-[9px] font-black bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full uppercase">{pass.doc_id || 'ID'}</span>
                            <span className="text-[9px] text-slate-400 font-bold">{new Date(pass.created_at).toLocaleDateString()}</span>
                          </div>
                          <p className="text-xs font-bold text-slate-800 mb-3 line-clamp-1 italic">"{pass.request_text || 'Demande sans titre'}"</p>
                          <div className="flex gap-2">
                            <button onClick={() => downloadExistingPassport(pass)} className="flex-1 bg-slate-50 text-slate-600 hover:bg-slate-900 hover:text-white py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2"><Download size={12} /> Télécharger</button>
                            <button onClick={() => deletePassport(pass.id)} className="px-3 bg-red-50 text-red-500 hover:bg-red-600 hover:text-white rounded-lg transition-all flex items-center justify-center"><Trash2 size={14} /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-slate-100/50 rounded-2xl p-6 text-center border border-dashed border-slate-200 text-slate-400 text-xs italic">
                      Aucun passeport généré.
                    </div>
                  )}
                </div>
              </div>

              <div className="lg:col-span-2 space-y-6">
                
                {/* WIDGET : ALERTES FORFEO FINDS */}
                <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm relative">
                   <div className="flex justify-between items-center mb-4">
                     <h3 className="font-black text-slate-900 flex items-center gap-2 uppercase tracking-widest text-xs"><Bell size={16} className="text-blue-500" /> Mes Alertes Finds</h3>
                     <Link to="/forfeo-finds" className="text-[10px] font-bold text-blue-600 hover:underline">Voir les offres</Link>
                   </div>
                   
                   {myAlerts.length > 0 ? (
                     <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                       {myAlerts.map(alert => (
                         <div key={alert.id} className="bg-slate-50 border border-slate-200 p-3 rounded-xl flex justify-between items-start group">
                           <div>
                             <div className="text-xs font-black text-slate-800 mb-1">{alert.region || 'Toutes régions'}</div>
                             <div className="text-[10px] text-slate-500 flex gap-2">
                               {alert.category_type && <span className="bg-white px-1.5 py-0.5 rounded border border-slate-200">{alert.category_type}</span>}
                               {alert.budget && <span className="bg-white px-1.5 py-0.5 rounded border border-slate-200">{alert.budget}</span>}
                             </div>
                           </div>
                           <button onClick={() => deleteAlert(alert.id)} className="text-slate-300 hover:text-red-500 transition"><Trash2 size={14} /></button>
                         </div>
                       ))}
                     </div>
                   ) : (
                     <div className="text-xs text-slate-500 bg-slate-50 p-4 rounded-xl text-center border border-dashed border-slate-200">
                        Aucune alerte active. <br/>Créez-en une depuis <Link to="/forfeo-finds" className="text-blue-600 font-bold">Forfeo Finds</Link>.
                     </div>
                   )}
                </div>

                <div className="flex justify-between items-end px-2 pt-4">
                  <h3 className="font-black text-slate-900 flex items-center gap-2 uppercase tracking-widest text-xs"><Flame size={16} className="text-orange-500" /> Récemment Consulté</h3>
                </div>

                {seenKeys.size > 0 ? (
                   <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center"><Eye size={20} /></div>
                        <div>
                          <p className="text-sm font-black text-slate-900">Vous avez consulté {seenKeys.size} offres récemment.</p>
                          <p className="text-xs text-slate-500">Retournez sur Forfeo Finds pour les retrouver.</p>
                        </div>
                      </div>
                      <Link to="/forfeo-finds?hideseen=true" className="bg-slate-900 text-white px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition">Continuer</Link>
                   </div>
                ) : (
                   <div className="bg-slate-100/50 rounded-2xl p-6 text-center border border-dashed border-slate-200 text-slate-400 text-xs italic">
                     Historique vide.
                   </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* TAB : FAVORIS (UNIFIÉS) */}
        {activeTab === 'favorites' && (
          <div className="max-w-6xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-12">
            
            {/* 1. FAVORIS FORFEO FINDS (Offres Flash / Deals) */}
            <div>
               <h3 className="font-black text-slate-900 text-xl mb-6 flex items-center gap-2"><Flame className="text-orange-500" /> Offres & Deals Sauvegardés</h3>
               {findsFavorites.length > 0 ? (
                 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                   {findsFavorites.map(fav => {
                     const d = fav.details;
                     return (
                       <div key={fav.id} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-200 group hover:shadow-md transition flex flex-col cursor-pointer" onClick={() => navigate(d?.public_url || `/last-call?src=${fav.source}&id=${fav.source_id}`)}>
                         <div className="h-32 bg-slate-900 relative">
                           {d?.image_url || d?.imageUrl ? (
                             <img src={d.image_url || d.imageUrl} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition" alt="" />
                           ) : <div className="w-full h-full flex items-center justify-center text-slate-500"><Store /></div>}
                           <button onClick={(e) => deleteFindsFavorite(fav.id, e)} className="absolute top-2 right-2 bg-white/90 text-red-500 p-1.5 rounded-full hover:bg-red-50 transition shadow"><Heart size={14} fill="currentColor" /></button>
                         </div>
                         <div className="p-4 flex-1 flex flex-col">
                           <h4 className="font-black text-slate-900 text-sm line-clamp-2 leading-tight mb-1">{d?.title || 'Offre'}</h4>
                           <div className="text-[10px] text-slate-500 font-bold mb-2 flex justify-between">
                             <span>{d?.region || d?.region_hint || 'Réseau'}</span>
                             {d?.price && <span className="text-slate-900">{d.price}$</span>}
                           </div>
                           <div className="mt-auto pt-3 border-t border-slate-50 text-[9px] font-black uppercase text-blue-600 flex items-center gap-1 group-hover:underline">
                             Voir l'offre <ChevronRight size={10} />
                           </div>
                         </div>
                       </div>
                     )
                   })}
                 </div>
               ) : (
                 <div className="bg-white rounded-3xl p-10 border border-slate-200 text-center flex flex-col items-center">
                    <div className="w-16 h-16 bg-orange-50 rounded-full flex items-center justify-center mb-4 text-orange-400"><Flame size={24} /></div>
                    <p className="text-sm text-slate-500 font-medium mb-4">Aucune offre sauvegardée.</p>
                    <Link to="/forfeo-finds" className="bg-orange-500 text-white px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-orange-600 transition">Explorer Forfeo Finds</Link>
                 </div>
               )}
            </div>

            {/* 2. FAVORIS CLASSIQUES (Lieux, Hôtels) */}
            <div className="border-t border-slate-200 pt-8">
              <h3 className="font-black text-slate-900 text-xl mb-6 flex items-center gap-2"><MapPin className="text-blue-600" /> Lieux & Hébergements</h3>
              {favorites.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {favorites.map((fav) => (
                    <div key={fav.id} onClick={() => handleVisitFavorite(fav)} className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all cursor-pointer group flex gap-4 relative">
                      <div className="w-20 h-20 rounded-xl bg-slate-100 overflow-hidden shrink-0 relative">
                        {fav.data?.image ? <img src={fav.data.image} alt={fav.data?.name} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-slate-200 flex items-center justify-center text-slate-400"><Store size={20} /></div>}
                        <div className={`absolute top-1 left-1 px-1.5 py-0.5 rounded text-[8px] font-black uppercase text-white ${
                          fav.type === 'local_offer' ? 'bg-emerald-600' : 
                          fav.type === 'boutique_forfait' ? 'bg-purple-600' : 'bg-blue-600'
                        }`}>
                          {fav.type === 'local_offer' ? <Tag size={8} /> : 
                           fav.type === 'boutique_forfait' ? <Store size={8} /> : <Hotel size={8} />}
                        </div>
                      </div>
                      <div className="flex-1 overflow-hidden flex flex-col justify-center">
                        <h4 className="font-black text-slate-900 text-sm truncate leading-tight mb-1">{fav.data?.name || 'Favori'}</h4>
                        <p className="text-[10px] text-slate-500 font-medium line-clamp-2">{fav.data?.description || fav.data?.location || 'Québec'}</p>
                        <span className="text-[9px] font-black mt-2 text-blue-600 group-hover:underline flex items-center gap-1 uppercase tracking-widest">
                          Voir la fiche <ChevronRight size={8} />
                        </span>
                      </div>
                      <button onClick={(e) => deleteFavorite(fav.id, e)} className="absolute top-2 right-2 text-slate-300 hover:text-red-500 transition p-1 bg-white rounded-full shadow-sm z-10"><Trash2 size={14} /></button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-3xl p-10 border border-slate-200 text-center flex flex-col items-center">
                  <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4 text-blue-300"><Map size={24} /></div>
                  <p className="text-sm text-slate-500 font-medium mb-4">Aucun lieu sauvegardé.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB : TRIPS */}
        {activeTab === 'trips' && (
          <div className="max-w-4xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-12">
            
            <div className="bg-white rounded-3xl p-6 border border-orange-100 shadow-sm flex flex-col md:flex-row md:items-center md:justify-between gap-5 relative overflow-hidden">
              <div className="absolute -right-12 -top-12 w-40 h-40 bg-orange-200 rounded-full blur-3xl opacity-40 pointer-events-none"></div>
              <div className="relative z-10">
                <div className="text-[10px] font-black uppercase tracking-widest text-orange-600 mb-2 flex items-center gap-2"><ShoppingBag size={14} /> Page recommandée</div>
                <h3 className="text-xl font-black text-slate-900">Vos achats complets sont maintenant dans “Mes achats”.</h3>
                <p className="text-sm text-slate-500 mt-1">Vous y trouverez les articles, taxes, reçus et vouchers QR Forfeo.</p>
              </div>
              <button onClick={() => navigate('/mes-achats')} className="relative z-10 bg-orange-500 text-white px-6 py-3 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-orange-600 transition flex items-center justify-center gap-2">
                Voir mes achats <ExternalLink size={15} />
              </button>
            </div>

            {/* COMMANDES & BONS D'ÉCHANGE (Vouchers/Boutique/LastCall) */}
            <div>
              <h3 className="font-black text-slate-900 text-xl mb-6 flex items-center gap-2"><ShoppingBag className="text-orange-500" /> Mes Achats & Forfaits Boutique</h3>
              {orders.length > 0 ? (
                <div className="space-y-4">
                  {orders.map(order => {
                    const isVoucher = order.delivery_method === 'pickup';
                    const hasBoutiqueItem = order.items?.some((i:any) => i.type === 'boutique_forfait');
                    const forfaitItem = order.items?.find((i:any) => i.type === 'boutique_forfait');

                    return (
                      <div key={order.id} className="bg-white rounded-3xl p-6 shadow-sm border border-slate-200 flex flex-col md:flex-row gap-6 relative overflow-hidden group hover:shadow-md transition">
                        <div className={`absolute left-0 top-0 bottom-0 w-2 ${order.status === 'picked_up' ? 'bg-green-500' : hasBoutiqueItem ? 'bg-purple-500' : 'bg-orange-500'}`}></div>
                        
                        <div className="flex-1 pl-4">
                          <div className="flex justify-between items-start mb-4">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${hasBoutiqueItem ? 'bg-purple-100' : 'bg-slate-100'}`}>
                                {hasBoutiqueItem ? <Store className="text-purple-600" size={18} /> : <ShoppingBag className="text-slate-400" size={18} />}
                              </div>
                              <div>
                                <h4 className="font-black text-slate-900 text-sm uppercase tracking-tight">{order.business?.name || "Commerce partenaire"}</h4>
                                <p className="text-[10px] text-slate-500 font-bold">{new Date(order.created_at).toLocaleDateString()} • ID: {order.id.slice(0, 8)}</p>
                              </div>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase ${
                              order.status === 'picked_up' ? 'bg-green-100 text-green-700' : 
                              isVoucher ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
                            }`}>
                              {order.status === 'picked_up' ? 'Utilisé / Récupéré' : isVoucher ? 'Prêt à utiliser' : 'En préparation'}
                            </span>
                          </div>

                          <div className="space-y-2 mb-4 bg-slate-50 p-3 rounded-xl border border-slate-100">
                            {order.items && order.items.map((item: any, i: number) => (
                              <div key={i} className="flex justify-between items-center text-xs font-bold text-slate-700">
                                <div className="flex items-center gap-2">
                                  {item.type === 'boutique_forfait' && <span className="bg-purple-600 text-white px-1.5 py-0.5 rounded text-[8px] uppercase tracking-wider">Forfait</span>}
                                  {item.type === 'last_call' && <span className="bg-red-500 text-white px-1.5 py-0.5 rounded text-[8px] uppercase tracking-wider">Flash</span>}
                                  <span>{item.quantity}x {item.title || item.name}</span>
                                </div>
                                <span>{(item.price * item.quantity).toFixed(2)}$</span>
                              </div>
                            ))}
                            <div className="border-t border-slate-200 pt-2 flex justify-between font-black text-slate-900 text-sm">
                              <span>Total</span>
                              <span>{order.total_amount}$</span>
                            </div>
                          </div>

                          {/* AFFICHAGE DU QR CODE */}
                          {isVoucher && order.pickup_code && order.status !== 'picked_up' && (
                            <div className="flex flex-col sm:flex-row items-center gap-4 bg-slate-900 text-white p-4 rounded-xl shadow-lg relative overflow-hidden">
                              <div className="absolute -right-10 -top-10 w-32 h-32 bg-white opacity-5 rounded-full blur-2xl"></div>
                              <div className="bg-white p-1.5 rounded-xl shrink-0">
                                <img src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${order.pickup_code}&color=000000`} alt="QR Code d'échange" className="w-16 h-16 sm:w-20 sm:h-20" />
                              </div>
                              <div className="text-center sm:text-left flex-1">
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">
                                  {hasBoutiqueItem ? 'Code d\'utilisation (Voucher)' : 'Code de Ramassage'}
                                </p>
                                <p className="font-mono text-2xl sm:text-3xl font-black text-white tracking-widest">{order.pickup_code}</p>
                                <p className="text-[9px] text-slate-400 mt-1">À présenter chez le commerçant.</p>
                              </div>
                            </div>
                          )}
                          
                          {/* BOUTON AVIS */}
                          {order.status === 'picked_up' && (
                             <div className="bg-green-50 border border-green-100 p-4 rounded-xl flex flex-col md:flex-row items-center justify-between gap-4">
                               <div className="text-green-700 text-sm font-bold flex items-center gap-2">
                                 <CheckCircle size={16} /> Ce bon a été utilisé.
                               </div>
                               {hasBoutiqueItem && forfaitItem?.id && (
                                 <button
                                    onClick={() => setReviewModal({ isOpen: true, order, forfaitId: forfaitItem.id, rating: 5, comment: '' })}
                                    className="bg-white border-2 border-yellow-400 text-yellow-600 hover:bg-yellow-50 px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest transition flex items-center gap-2 shadow-sm"
                                 >
                                   <Star size={14} fill="currentColor" /> Évaluer (+10 pts)
                                 </button>
                               )}
                             </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="text-center py-10 bg-slate-50 rounded-3xl border border-dashed border-slate-200 text-slate-400 italic">Aucun achat ou forfait récent.</div>
              )}
            </div>

            {internalBookings.length > 0 && (
              <div className="mb-8 border-t border-slate-200 pt-8">
                <h3 className="font-black text-slate-900 text-xl mb-6 flex items-center gap-2"><Bed className="text-blue-600" /> Réservations Hébergement</h3>
                <div className="space-y-6">
                  {internalBookings.map((res) => (
                    <div key={res.id} className="bg-white rounded-3xl border border-slate-200 shadow-lg overflow-hidden flex flex-col md:flex-row relative group hover:shadow-xl transition-all">
                      <div className={`absolute left-0 top-0 bottom-0 w-2 ${res.status === 'confirmed' ? 'bg-green-500' : 'bg-yellow-500'}`}></div>

                      <div className="p-6 flex-1 pl-8">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                          <div className="flex items-center gap-4">
                            <div className="w-16 h-16 bg-slate-100 rounded-2xl overflow-hidden shadow-sm">
                              {res.business?.image_url ? (
                                <img src={res.business.image_url} className="w-full h-full object-cover" alt="Business" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-slate-300"><Hotel size={24} /></div>
                              )}
                            </div>
                            <div>
                              <h4 className="font-black text-xl text-slate-900">{res.business?.name || "Hôtel Partenaire"}</h4>
                              <p className="text-xs text-slate-500 font-bold flex items-center gap-1">
                                <MapPin size={10} /> {res.business?.region || 'Québec'}
                              </p>
                            </div>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${res.status === 'confirmed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                            {res.status === 'confirmed' ? 'Confirmé' : 'En Attente'}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 gap-6 bg-slate-50 p-4 rounded-2xl border border-slate-100">
                          <div>
                            <p className="text-[9px] text-slate-400 font-black uppercase tracking-widest mb-1">ARRIVÉE</p>
                            <p className="font-bold text-slate-900 text-sm flex items-center gap-2">
                              <Calendar size={14} className="text-blue-500" />
                              {new Date(res.check_in_date).toLocaleDateString()}
                            </p>
                          </div>
                          <div>
                            <p className="text-[9px] text-slate-400 font-black uppercase tracking-widest mb-1">DÉPART</p>
                            <p className="font-bold text-slate-900 text-sm flex items-center gap-2">
                              <Clock size={14} className="text-orange-500" />
                              {new Date(res.check_out_date).toLocaleDateString()}
                            </p>
                          </div>
                        </div>

                        <div className="mt-4 flex items-center gap-2 text-[10px] text-slate-400 font-medium">
                          <span className="bg-slate-100 px-2 py-0.5 rounded text-slate-500">ID: {res.id.slice(0, 8)}</span>
                          <span className="bg-blue-50 text-blue-600 px-2 py-0.5 rounded">Paiement Forfeo</span>
                        </div>
                      </div>

                      <div className="bg-slate-900 p-6 flex flex-col items-center justify-center text-center md:w-48 border-l border-dashed border-slate-700 relative">
                        <div className="absolute left-0 top-0 bottom-0 w-[1px] border-l-2 border-dashed border-slate-700 -ml-[1px]"></div>
                        <div className="absolute top-0 left-0 w-4 h-4 bg-white rounded-br-full -translate-x-1/2 -translate-y-1/2"></div>
                        <div className="absolute bottom-0 left-0 w-4 h-4 bg-white rounded-tr-full -translate-x-1/2 translate-y-1/2"></div>

                        <p className="text-white/50 text-[9px] font-black uppercase tracking-widest mb-2">SCANNEZ ICI</p>
                        <div className="bg-white p-2 rounded-xl">
                          <img src={`https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${res.id}&color=000000`} alt="Ticket QR" className="w-20 h-20" />
                        </div>
                        <p className="text-white text-[9px] font-mono mt-2 opacity-60">Billet Électronique</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {unifiedBookings.length > 0 && (
              <div className="border-t border-slate-200 pt-8">
                <h3 className="font-black text-slate-900 text-xl mb-6 flex items-center gap-2"><Ticket className="text-purple-600" /> Autres Billets & Activités</h3>
                <div className="space-y-6">
                  {unifiedBookings.map((res) => (
                    <div key={res.id} className="bg-white rounded-3xl border border-slate-200 shadow-md overflow-hidden flex flex-col md:flex-row relative group">
                      <div className="p-6 flex-1 pl-8">
                        <div className="flex items-center gap-4 mb-4">
                          <div className="w-12 h-12 bg-slate-100 rounded-2xl overflow-hidden shadow-sm flex items-center justify-center">
                            <Store size={20} className="text-slate-400" />
                          </div>
                          <div>
                            <h4 className="font-black text-lg text-slate-900">{res.business?.name || "Activité"}</h4>
                            <p className="text-xs text-slate-500 font-bold">{new Date(res.start_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <span className="bg-purple-50 text-purple-600 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-widest">Réservation Externe</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* TAB 2 : REWARDS */}
        {activeTab === 'rewards' && (
          <div className="max-w-5xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-black text-slate-900 uppercase tracking-tighter mb-2 flex items-center justify-center gap-2"><Gift className="text-yellow-500" /> Boutique Forfeo Récompense</h2>
              <p className="text-slate-500 text-sm">Échangez vos points contre des avantages exclusifs chez nos partenaires.</p>
              <div className="mt-4 inline-flex bg-white rounded-full px-4 py-2 shadow-sm border border-slate-200 items-center gap-2">
                <span className="text-xs font-bold text-slate-400 uppercase">Votre solde :</span>
                <span className="text-lg font-black text-yellow-600">{profile?.points_total || 0} pts</span>
              </div>
            </div>

            {rewardsList.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {rewardsList.map((reward) => (
                  <div key={reward.id} className="bg-white rounded-3xl border border-slate-100 shadow-lg overflow-hidden flex flex-col group relative hover:-translate-y-1 transition-all">
                    <div className="absolute top-0 right-0 bg-yellow-400 text-white text-[10px] font-black px-3 py-1 rounded-bl-xl uppercase shadow-md">{reward.cost_points} PTS</div>
                    <div className="p-6 flex-1 flex flex-col">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center"><Store size={14} className="text-slate-400" /></div>
                        <span className="text-xs font-bold text-slate-500">{reward.businesses?.name}</span>
                      </div>
                      <h3 className="font-black text-lg text-slate-900 leading-tight mb-2">{reward.title}</h3>
                      <p className="text-slate-600 text-sm leading-relaxed mb-6 flex-1">{reward.description}</p>
                      <div className="pt-4 border-t border-slate-50">
                        <button
                          onClick={() => setShowMyCard(true)}
                          disabled={(profile?.points_total || 0) < reward.cost_points}
                          className={`w-full py-3 rounded-xl text-xs font-black uppercase tracking-widest transition flex items-center justify-center gap-2 ${(profile?.points_total || 0) >= reward.cost_points ? 'bg-slate-900 text-white hover:bg-slate-800 shadow-lg' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}
                        >
                          {(profile?.points_total || 0) >= reward.cost_points ? <><QrCode size={14} /> Utiliser</> : `Manque ${reward.cost_points - (profile?.points_total || 0)} pts`}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-slate-50 rounded-3xl p-12 text-center border-2 border-dashed border-slate-200">
                <Gift size={48} className="text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-slate-500">Aucune récompense disponible</h3>
                <p className="text-xs text-slate-400">Les entreprises partenaires ajouteront bientôt leurs offres.</p>
              </div>
            )}

            <div className="mt-12 text-center">
              <Link to="/terms-loyalty" className="text-[10px] font-bold text-slate-400 hover:text-blue-600 flex items-center justify-center gap-1">
                <ShieldCheck size={10} /> Conditions d'utilisation du programme Forfeo Récompense
              </Link>
            </div>
          </div>
        )}

        {/* TAB 3 : LOCAL */}
        {activeTab === 'local' && (
          <div className="max-w-5xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-black text-slate-900 uppercase tracking-tighter mb-2 flex items-center justify-center gap-2"><Store className="text-emerald-600" /> Vitrine du Réseau Local</h2>
              <p className="text-slate-500 text-sm">Découvrez les offres exclusives des commerçants près de chez vous.</p>
            </div>

            {localPosts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {localPosts.map((post) => (
                  <div key={post.id} className="bg-white rounded-3xl border border-slate-100 shadow-lg overflow-hidden flex flex-col group hover:-translate-y-1 transition-all duration-300">
                    {post.image_url && (
                      <div className="h-40 bg-slate-200 relative overflow-hidden">
                        <img src={post.image_url} alt="Offre" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                        <div className="absolute top-3 left-3 bg-emerald-600 text-white text-[10px] font-black px-2 py-1 rounded uppercase shadow-md">{post.offer_type || 'OFFRE'}</div>
                      </div>
                    )}
                    <div className="p-6 flex-1 flex flex-col">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="font-black text-lg text-slate-900 leading-tight mb-1">{post.businesses?.name || "Partenaire Local"}</h3>
                          <span className="text-xs text-slate-400 font-bold flex items-center gap-1"><MapPin size={10} /> Réseau Forfeo</span>
                        </div>
                      </div>

                      <p className="text-slate-600 text-sm leading-relaxed mb-6 flex-1">{post.content}</p>

                      <div className="flex justify-between items-center pt-4 border-t border-slate-50 gap-2">
                        <div className="flex gap-2">
                          <button
                            onClick={() => startChatWithBusiness(post.businesses?.id)}
                            className="text-[10px] font-black text-blue-600 bg-blue-50 px-3 py-1.5 rounded-full flex items-center gap-1 hover:bg-blue-600 hover:text-white transition"
                          >
                            <MessageCircle size={12} /> Contacter (+25pts)
                          </button>
                          <Link
                            to={`/place/${post.businesses?.id}`}
                            className="text-[10px] font-black text-slate-600 bg-slate-100 px-3 py-1.5 rounded-full flex items-center gap-1 hover:bg-slate-200 transition"
                          >
                            <Eye size={12} /> Fiche
                          </Link>
                        </div>

                        <button
                          onClick={() => toggleLikeLocalPost(post)}
                          className={`p-2 rounded-full transition shadow-sm ${isPostLiked(post.id) ? 'bg-red-50 text-red-600 hover:bg-slate-100' : 'bg-slate-50 text-slate-400 hover:text-red-500 hover:bg-red-50'}`}
                          title="Favori +5pts"
                        >
                          <Heart size={20} fill={isPostLiked(post.id) ? "currentColor" : "none"} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-slate-50 rounded-3xl p-12 text-center border-2 border-dashed border-slate-200">
                <Store size={48} className="text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-slate-500">Aucune offre pour le moment</h3>
                <p className="text-xs text-slate-400">Les entreprises locales publieront bientôt leurs offres ici.</p>
              </div>
            )}
          </div>
        )}

        {/* TAB 4 : NEWS */}
        {activeTab === 'news' && (
          <div className="max-w-4xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 rounded-3xl p-1 shadow-2xl mb-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-[22px] p-8 text-white text-center">
                <div className="inline-block bg-white text-indigo-600 p-3 rounded-full shadow-lg mb-4 animate-bounce"><Zap size={24} fill="currentColor" /></div>
                <h2 className="text-3xl font-black uppercase tracking-tighter mb-2">Nouvelles du Réseau Admin</h2>
                <p className="text-purple-100 text-sm font-medium">Annonces officielles de la plateforme Forfeo.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {newsFeed.map((news) => (
                <div key={news.id} onClick={() => setSelectedNews(news)} className="bg-white text-slate-900 p-5 rounded-2xl shadow-md border-l-4 border-pink-500 flex flex-col justify-between hover:scale-[1.01] transition-transform cursor-pointer">
                  <div>
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-slate-200 overflow-hidden shadow-sm">
                          <img src={`https://ui-avatars.com/api/?name=${news.author_name || 'Forfeo'}&background=random&color=fff`} alt="Author" />
                        </div>
                        <div>
                          <span className="block text-[11px] font-black text-slate-800 leading-none">{news.author_name || 'Partenaire Forfeo'}</span>
                          <span className="block text-[9px] font-bold text-slate-400">Admin</span>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded text-[9px] font-black uppercase ${news.tag === 'PROMO' ? 'bg-pink-100 text-pink-600' : 'bg-indigo-100 text-indigo-600'}`}>{news.tag || 'INFO'}</span>
                    </div>
                    <h3 className="font-black text-lg mb-2 leading-tight">{news.title}</h3>
                    <p className="text-xs text-slate-600 leading-relaxed line-clamp-3 mb-4">{news.content}</p>
                  </div>
                  <div className="pt-3 border-t border-slate-100 flex justify-between items-center">
                    <span className="text-[10px] text-slate-400 font-bold flex items-center gap-1"><Calendar size={10} /> {new Date(news.created_at).toLocaleDateString()}</span>
                    <button className="text-[10px] font-black text-white bg-indigo-600 px-4 py-1.5 rounded-full flex items-center gap-1 hover:bg-indigo-700 transition">Lire <ChevronRight size={10} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TAB 5 : MESSAGES */}
        {activeTab === 'messages' && (
          <div className="max-w-5xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="text-center mb-6">
              <h2 className="text-3xl font-black text-slate-900 uppercase tracking-tighter mb-2 flex items-center justify-center gap-2"><MessageCircle className="text-blue-600" /> Messagerie Privée</h2>
              <p className="text-slate-500 text-sm">Discutez en direct avec les commerçants du réseau.</p>
            </div>
            <ChatSystem
              userType="traveler"
              userId={sessionUser?.id}
              initialContactId={searchParams.get('contact')}
            />
          </div>
        )}

        {/* TAB 6 : LANGUES */}
        {activeTab === 'language' && (
          <div className="max-w-6xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* HERO WOW */}
            <div className="relative overflow-hidden rounded-[32px] border border-slate-200 shadow-2xl bg-white mb-8">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-600 via-pink-600 to-amber-400 opacity-10"></div>
              <div className="absolute -top-24 -right-24 w-72 h-72 bg-purple-500 rounded-full blur-3xl opacity-30"></div>
              <div className="absolute -bottom-28 -left-20 w-80 h-80 bg-pink-500 rounded-full blur-3xl opacity-25"></div>

              <div className="relative p-8 md:p-10">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-3xl bg-gradient-to-br from-purple-600 to-pink-600 text-white flex items-center justify-center shadow-xl ring-4 ring-white">
                      <Globe size={30} />
                    </div>
                    <div>
                      <div className="inline-flex items-center gap-2 bg-slate-900 text-white px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest shadow">
                        <Sparkles size={12} /> Forfy Language Lab • 2026
                      </div>
                      <h1 className="text-3xl md:text-4xl font-black text-slate-900 uppercase tracking-tighter mt-3">
                        Apprends une Langue avec <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">Forfy</span>
                      </h1>
                      <p className="text-slate-600 text-sm md:text-lg mt-2 max-w-3xl">
                        Parle, écoute, pratique. Maintenant avec <span className="font-black">voix OpenAI HD</span> (naturelles, non robotiques) + mode “coach”.
                      </p>
                    </div>
                  </div>

                  <div className="w-full md:w-auto bg-white/70 backdrop-blur rounded-3xl border border-slate-200 p-4 shadow-lg">
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-xs font-black uppercase tracking-widest text-slate-500">Voix</div>
                        <select
                          value={preferredVoice}
                          onChange={(e) => setPreferredVoice(e.target.value)}
                          className="text-xs font-bold bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-700 outline-none focus:ring-2 focus:ring-purple-200"
                        >
                          {voiceOptions.map(v => (
                            <option key={v.id} value={v.id}>{v.label}</option>
                          ))}
                        </select>
                      </div>

                      <div className="flex items-center justify-between gap-3">
                        <div className="text-xs font-black uppercase tracking-widest text-slate-500">Voix HD</div>
                        <button
                          onClick={() => setTtsEnabled(v => !v)}
                          className={`px-3 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition border ${ttsEnabled ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200'}`}
                        >
                          {ttsEnabled ? 'Activé' : 'Désactivé'}
                        </button>
                      </div>

                      <div className="flex gap-3">
                        <button
                          onClick={() => setShowLanguageGuide(true)}
                          className="flex-1 bg-white text-slate-800 border border-slate-200 px-4 py-3 rounded-2xl text-xs font-black uppercase tracking-widest hover:shadow-md transition"
                        >
                          Guide
                        </button>
                        <button
                          onClick={() => awardPoints(50, 'LANGUAGE_START', 'Démarrage apprentissage langues')}
                          className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-600 text-white px-4 py-3 rounded-2xl text-xs font-black uppercase tracking-widest hover:shadow-xl transition"
                        >
                          +50 pts
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="bg-white/70 backdrop-blur rounded-2xl border border-slate-200 p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-purple-100 text-purple-700 flex items-center justify-center"><Mic size={18} /></div>
                    <div>
                      <div className="text-xs font-black text-slate-900">Micro</div>
                      <div className="text-[11px] text-slate-500 font-medium">Reconnaissance vocale navigateur</div>
                    </div>
                  </div>
                  <div className="bg-white/70 backdrop-blur rounded-2xl border border-slate-200 p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-amber-100 text-amber-700 flex items-center justify-center"><Volume2 size={18} /></div>
                    <div>
                      <div className="text-xs font-black text-slate-900">Voix</div>
                      <div className="text-[11px] text-slate-500 font-medium">OpenAI TTS HD</div>
                    </div>
                  </div>
                  <div className="bg-white/70 backdrop-blur rounded-2xl border border-slate-200 p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center"><Trophy size={18} /></div>
                    <div>
                      <div className="text-xs font-black text-slate-900">Points</div>
                      <div className="text-[11px] text-slate-500 font-medium">Gagne en pratiquant</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Guide modal */}
            {showLanguageGuide && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/90 backdrop-blur-md animate-in zoom-in duration-200">
                <div className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl overflow-hidden">
                  <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white flex justify-between items-center">
                    <div>
                      <h3 className="text-2xl font-black mb-2">Guide du débutant en langues</h3>
                      <p className="text-blue-100">Comment tirer le meilleur parti de Forfy</p>
                    </div>
                    <button onClick={() => setShowLanguageGuide(false)} className="bg-white/20 hover:bg-white/40 p-2 rounded-full transition">
                      <X size={20} />
                    </button>
                  </div>
                  <div className="p-6">
                    <div className="space-y-4">
                      <div className="flex items-start gap-4">
                        <div className="bg-blue-100 text-blue-600 p-3 rounded-full">
                          <Mic size={20} />
                        </div>
                        <div>
                          <h4 className="font-black text-slate-900 mb-1">Utilise le micro</h4>
                          <p className="text-slate-600 text-sm">Parle naturellement, Forfy corrige et te coach en temps réel.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="bg-purple-100 text-purple-600 p-3 rounded-full">
                          <Volume2 size={20} />
                        </div>
                        <div>
                          <h4 className="font-black text-slate-900 mb-1">Écoute et répète</h4>
                          <p className="text-slate-600 text-sm">Les voix OpenAI HD sont plus naturelles que le TTS navigateur.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="bg-green-100 text-green-600 p-3 rounded-full">
                          <MessageSquare size={20} />
                        </div>
                        <div>
                          <h4 className="font-black text-slate-900 mb-1">Pose des questions</h4>
                          <p className="text-slate-600 text-sm">Demande “Comment dit-on…” et entraîne-toi comme en voyage.</p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-8 pt-6 border-t border-slate-200">
                      <div className="bg-gradient-to-r from-green-50 to-emerald-100 p-4 rounded-2xl">
                        <p className="text-sm font-bold text-emerald-800">
                          💡 <span className="font-black">Astuce :</span> 5 phrases/jour → 150 phrases/mois.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="p-6 bg-slate-50 flex justify-between">
                    <button
                      onClick={() => {
                        setShowLanguageGuide(false);
                        awardPoints(25, 'READ_GUIDE', 'Lecture guide langues');
                      }}
                      className="px-6 py-3 bg-gradient-to-r from-slate-900 to-slate-800 text-white rounded-xl text-sm font-black uppercase tracking-widest"
                    >
                      C'est parti ! (+25 pts)
                    </button>
                    <button
                      onClick={() => setShowLanguageGuide(false)}
                      className="px-6 py-3 bg-white text-slate-600 border border-slate-300 rounded-xl text-sm font-bold"
                    >
                      Fermer
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Assistant principal */}
            <div className="mt-8">
              <LanguageAssistant
                userId={sessionUser?.id}
                preferredVoice={preferredVoice}
                ttsEnabled={ttsEnabled}
              />
            </div>
          </div>
        )}

      </div>
      <Footer />
    </div>
  );
};

export default ClubForfeo;
